import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Ban, Plus, Search, Send, Trash2, Truck } from "lucide-react";

import {
  useInventory,
  usePurchaseOrder,
  useReceipts,
  useRemoveOrderLine,
  useSetOrderLine,
  useSetOrderStatus,
} from "@/api/queries";
import type { OrderLine } from "@/api/types";
import { useCanWrite } from "@/auth/useAuth";
import { Button } from "@/components/Button";
import { Card, CardBody, CardHeader } from "@/components/Card";
import { TableShell, Td, Th, Tr } from "@/components/DataTable";
import { Dialog } from "@/components/Dialog";
import { Field, Input, Select } from "@/components/Input";
import { EmptyState, ErrorState, LoadingState } from "@/components/States";
import { StatusBadge } from "@/components/StatusBadge";
import { useToast } from "@/components/toast-context";
import { Page } from "@/layout/Page";
import { PageHeader } from "@/layout/PageHeader";
import {
  formatDate,
  formatDateTime,
  formatMoney,
  formatPurchaseOrderStatus,
  formatQuantity,
  formatUnit,
} from "@/lib/format";
import { purchaseOrderTone } from "@/lib/stock";

/**
 * One purchase order (Frontend Spec §19, PHASE 10).
 *
 * The columns are the ones the spec names — commandé, reçu, backorder, prix —
 * and every one of them except *commandé* and *prix* is summed by the server
 * from the receipts. The screen never adds up a delivery itself.
 */
export function PurchaseOrderPage() {
  const { orderId = "" } = useParams();
  const navigate = useNavigate();
  const canWrite = useCanWrite();
  const toast = useToast();

  const { data, isLoading, error, refetch } = usePurchaseOrder(orderId);
  const { data: receipts } = useReceipts({ order_id: orderId, limit: 20 });
  const setStatus = useSetOrderStatus();
  const removeLine = useRemoveOrderLine();
  const [adding, setAdding] = useState(false);

  if (isLoading) return <LoadingState label="Chargement de la commande…" />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;
  if (!data) return null;

  const { order, lines } = data;
  const isDraft = order.status === "DRAFT";
  const receivable = order.status === "ORDERED" || order.status === "PARTIALLY_RECEIVED";

  async function move(status: "ORDERED" | "CANCELLED") {
    try {
      await setStatus.mutateAsync({ orderId, status });
      toast.success(status === "ORDERED" ? "Commande envoyée" : "Commande annulée");
    } catch (caught) {
      toast.error("Changement refusé", String(caught));
    }
  }

  return (
    <Page>
      <PageHeader
        crumbs={[{ label: "Achats", to: "/purchasing" }, { label: order.code }]}
        title={order.supplier_name}
        subtitle={[order.code, order.reference].filter(Boolean).join(" · ")}
        actions={
          canWrite && (
            <>
              {isDraft && (
                <Button
                  variant="secondary"
                  onClick={() => void move("CANCELLED")}
                  disabled={setStatus.isPending}
                >
                  <Ban className="size-4" aria-hidden />
                  Annuler
                </Button>
              )}
              {isDraft ? (
                <Button
                  variant="primary"
                  onClick={() => void move("ORDERED")}
                  disabled={lines.length === 0 || setStatus.isPending}
                  title={lines.length === 0 ? "Ajoutez au moins une ligne." : undefined}
                >
                  <Send className="size-4" aria-hidden />
                  Envoyer la commande
                </Button>
              ) : (
                receivable && (
                  <Button variant="primary" onClick={() => navigate(`/receiving?order=${orderId}`)}>
                    <Truck className="size-4" aria-hidden />
                    Recevoir
                  </Button>
                )
              )}
            </>
          )
        }
      />

      <div className="flex min-w-0 flex-col gap-3">
        <div className="grid min-w-0 grid-cols-2 gap-3 @3xl:grid-cols-4">
          <Summary label="Statut">
            <StatusBadge tone={purchaseOrderTone(order.status)}>
              {formatPurchaseOrderStatus(order.status)}
            </StatusBadge>
          </Summary>
          <Summary label="Total">{formatMoney(order.total)}</Summary>
          <Summary label="Commandé le">{formatDate(order.ordered_on)}</Summary>
          <Summary label="Attendu">{formatDate(order.expected_on)}</Summary>
        </div>

        <Card>
          <CardHeader
            title="Lignes"
            action={
              canWrite &&
              order.status !== "CANCELLED" && (
                <Button variant="secondary" size="sm" onClick={() => setAdding(true)}>
                  <Plus className="size-4" aria-hidden />
                  Ajouter une ligne
                </Button>
              )
            }
          />
          <div className="min-w-0">
            {lines.length === 0 ? (
              <EmptyState
                icon={<Plus className="size-6" aria-hidden />}
                title="Aucune ligne"
                description="Ajoutez ce que vous commandez, avec la quantité et le prix convenu."
              />
            ) : (
              <>
                <div className="hidden md:block">
                  <TableShell>
                    <thead>
                      <tr>
                        <Th className="max-w-0">Article</Th>
                        <Th align="right" className="whitespace-nowrap">
                          Commandé
                        </Th>
                        <Th align="right" className="whitespace-nowrap">
                          Reçu
                        </Th>
                        <Th align="right" className="hidden whitespace-nowrap @4xl:table-cell">
                          Quarantaine
                        </Th>
                        <Th align="right" className="whitespace-nowrap">
                          Backorder
                        </Th>
                        <Th align="right" className="hidden whitespace-nowrap @5xl:table-cell">
                          Prix unitaire
                        </Th>
                        <Th align="right" className="hidden whitespace-nowrap @3xl:table-cell">
                          Total
                        </Th>
                        {canWrite && <Th align="right">Action</Th>}
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((line) => (
                        <LineRow
                          key={line.line_id}
                          line={line}
                          canWrite={canWrite}
                          onRemove={async () => {
                            try {
                              await removeLine.mutateAsync({ orderId, lineId: line.line_id });
                            } catch (caught) {
                              toast.error("Ligne non retirée", String(caught));
                            }
                          }}
                        />
                      ))}
                    </tbody>
                  </TableShell>
                </div>

                <ul className="divide-y divide-border/60 md:hidden">
                  {lines.map((line) => (
                    <li key={line.line_id}>
                      <LineCard line={line} />
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </Card>

        {receipts && receipts.items.length > 0 && (
          <Card>
            <CardHeader title="Réceptions" />
            <CardBody className="flex flex-col gap-2">
              {receipts.items.map((receipt) => (
                <div
                  key={receipt.id}
                  className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b border-border/60 pb-2 text-[13px] last:border-0 last:pb-0"
                >
                  <span className="font-mono text-[12px] text-ink-muted">{receipt.code}</span>
                  <span className="text-ink-muted">{formatDateTime(receipt.received_at)}</span>
                  {receipt.supplier_reference && (
                    <span className="text-ink-subtle">BL {receipt.supplier_reference}</span>
                  )}
                  {receipt.mismatch_count > 0 && (
                    <StatusBadge tone="warning">
                      {receipt.mismatch_count} écart{receipt.mismatch_count > 1 ? "s" : ""}
                    </StatusBadge>
                  )}
                  {Number(receipt.quarantined_total) > 0 && (
                    <StatusBadge tone="out">
                      {formatQuantity(receipt.quarantined_total)} en quarantaine
                    </StatusBadge>
                  )}
                </div>
              ))}
            </CardBody>
          </Card>
        )}
      </div>

      <AddLineDialog open={adding} onOpenChange={setAdding} orderId={orderId} />
    </Page>
  );
}

function Summary({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-border bg-surface px-3 py-2.5">
      <p className="text-[11px] uppercase tracking-wide text-ink-subtle">{label}</p>
      <p className="tnum mt-1 text-[16px] font-semibold text-ink">{children}</p>
    </div>
  );
}

function LineRow({
  line,
  canWrite,
  onRemove,
}: {
  line: OrderLine;
  canWrite: boolean;
  onRemove: () => void;
}) {
  return (
    <Tr>
      <Td className="max-w-0">
        <Link to={`/products/${line.product_id}`} className="block min-w-0 hover:text-brand">
          <span className="block truncate font-medium text-ink">{line.product_name}</span>
          <span className="block truncate text-[11px] text-ink-subtle">
            {[line.product_manufacturer, line.product_part_number].filter(Boolean).join(" · ")}
          </span>
        </Link>
      </Td>
      <Td align="right" className="tnum whitespace-nowrap text-ink">
        {formatQuantity(line.ordered)}
        <span className="ml-1 text-[11px] text-ink-subtle">{formatUnit(line.unit)}</span>
      </Td>
      <Td align="right" className="tnum whitespace-nowrap">
        <span className={line.over_received ? "text-warning" : "text-ink-muted"}>
          {formatQuantity(line.received)}
        </span>
      </Td>
      <Td align="right" className="tnum hidden whitespace-nowrap @4xl:table-cell">
        <span className={Number(line.quarantined) > 0 ? "text-status-out" : "text-ink-subtle"}>
          {formatQuantity(line.quarantined)}
        </span>
      </Td>
      <Td align="right" className="tnum whitespace-nowrap">
        <span className={Number(line.backorder) > 0 ? "text-status-reserved" : "text-ink-subtle"}>
          {formatQuantity(line.backorder)}
        </span>
      </Td>
      <Td align="right" className="tnum hidden whitespace-nowrap text-ink-muted @5xl:table-cell">
        {formatMoney(line.unit_price)}
      </Td>
      <Td align="right" className="tnum hidden whitespace-nowrap text-ink @3xl:table-cell">
        {formatMoney(line.line_total)}
      </Td>
      {canWrite && (
        <Td align="right">
          {Number(line.received) === 0 && Number(line.quarantined) === 0 && (
            <Button variant="ghost" size="sm" onClick={onRemove} aria-label="Retirer la ligne">
              <Trash2 className="size-4" aria-hidden />
            </Button>
          )}
        </Td>
      )}
    </Tr>
  );
}

function LineCard({ line }: { line: OrderLine }) {
  return (
    <div className="flex flex-col gap-2 p-3">
      <div className="min-w-0">
        <p className="truncate text-[14px] font-medium text-ink">{line.product_name}</p>
        <p className="truncate text-[11px] text-ink-subtle">
          {[line.product_manufacturer, line.product_part_number].filter(Boolean).join(" · ")}
        </p>
      </div>
      <dl className="grid grid-cols-3 gap-x-3 text-[12px]">
        <div>
          <dt className="text-ink-subtle">Commandé</dt>
          <dd className="tnum text-ink">{formatQuantity(line.ordered)}</dd>
        </div>
        <div>
          <dt className="text-ink-subtle">Reçu</dt>
          <dd className="tnum text-ink-muted">{formatQuantity(line.received)}</dd>
        </div>
        <div>
          <dt className="text-ink-subtle">Backorder</dt>
          <dd
            className={`tnum ${Number(line.backorder) > 0 ? "text-status-reserved" : "text-ink-muted"}`}
          >
            {formatQuantity(line.backorder)}
          </dd>
        </div>
      </dl>
    </div>
  );
}

function AddLineDialog({
  open,
  onOpenChange,
  orderId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orderId: string;
}) {
  const toast = useToast();
  const setLine = useSetOrderLine();
  const [term, setTerm] = useState("");
  const [productId, setProductId] = useState("");
  const [quantity, setQuantity] = useState("");
  const [price, setPrice] = useState("");

  const { data: products } = useInventory({ q: term || undefined, limit: 25 }, { enabled: open });

  function reset() {
    setTerm("");
    setProductId("");
    setQuantity("");
    setPrice("");
  }

  async function submit() {
    if (!productId || !quantity) return;
    try {
      await setLine.mutateAsync({
        orderId,
        product_id: productId,
        quantity,
        unit_price: price || null,
      });
      toast.success("Ligne enregistrée");
      reset();
      onOpenChange(false);
    } catch (error) {
      toast.error("Ligne non enregistrée", String(error));
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
      title="Ajouter une ligne"
      description="Un article par ligne : ré-ajouter le même corrige la quantité."
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button
            variant="primary"
            onClick={() => void submit()}
            disabled={!productId || !quantity || setLine.isPending}
          >
            {setLine.isPending ? "Enregistrement…" : "Ajouter"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label="Article">
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-subtle"
              aria-hidden
            />
            <Input
              value={term}
              onChange={(event) => setTerm(event.target.value)}
              placeholder="Nom, fabricant, MPN…"
              className="pl-9"
              aria-label="Chercher un article"
            />
          </div>
          <Select
            value={productId}
            onChange={(event) => setProductId(event.target.value)}
            aria-label="Article commandé"
            className="mt-2"
          >
            <option value="">Choisir un article…</option>
            {products?.items.map((row) => (
              <option key={row.product_id} value={row.product_id}>
                {row.name}
                {row.part_number ? ` (${row.part_number})` : ""}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Quantité commandée">
          <Input
            type="number"
            min="0"
            step="any"
            value={quantity}
            onChange={(event) => setQuantity(event.target.value)}
            aria-label="Quantité commandée"
          />
        </Field>

        <Field
          label="Prix unitaire"
          hint="Facultatif. Sans prix, le total reste vide plutôt que d'afficher 0 $."
        >
          <Input
            type="number"
            min="0"
            step="any"
            value={price}
            onChange={(event) => setPrice(event.target.value)}
            aria-label="Prix unitaire"
          />
        </Field>
      </div>
    </Dialog>
  );
}
